<!--
  One title: a film, or a series with its seasons and episodes.

  The page has exactly one job — get you into the right thing quickly — so the
  primary action is a single full-width control that already knows whether it means
  "play", "resume", or "resume S2 E4". Everything below it is the material you use
  when the answer is not obvious.
-->
<script lang="ts">
  import type { CatalogItem, ContentRef, Episode, MediaKind } from '$types';
  import Art from '$lib/ui/Art.svelte';
  import Button from '$lib/ui/Button.svelte';
  import AwardMark from '$lib/ui/AwardMark.svelte';
  import CastRow from '$lib/ui/CastRow.svelte';
  import CertChip from '$lib/ui/CertChip.svelte';
  import Chip from '$lib/ui/Chip.svelte';
  import Empty from '$lib/ui/Empty.svelte';
  import EpisodeItem from '$lib/ui/EpisodeItem.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import Skeleton from '$lib/ui/Skeleton.svelte';
  import TrailerSheet from '$lib/ui/TrailerSheet.svelte';
  import Trailer from '$lib/ui/Trailer.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import Rail from '$lib/ui/Rail.svelte';
  import { awardsFor } from '$lib/ui/awards';
  import { certOf } from '$lib/ui/certs.svelte';
  import { catalog, detail, findEpisode, posterUrl, trailerStill, type TitleDetail } from '$lib/ui/cinemeta';
  import { prefetch } from '$lib/ui/api';
  import {gate}from '$lib/ui/session.svelte';
  import { count, episodeCode, remaining } from '$lib/ui/format';
  import { clearRoomEnded, endedRoom, go, nav, personHref, titleHref, watchHref } from '$lib/ui/nav.svelte';
  import { claimTopGround } from '$lib/ui/top-ground.svelte';
  import { isSaved, lastEpisodeOf, rate, ratingOf, rememberItem, resumeFor, toggleSaved } from '$lib/ui/prefs.svelte';
  import { shell } from '$lib/ui/shell.svelte';
  import {
    autoplayAllowed,
    claimTrailer,
    holdsTrailer,
    releaseTrailer,
    resumeSecFor,
    watchConditions
  } from '$lib/ui/trailer.svelte';
  import { bootDownloads, downloads, enqueue, enqueueMany, entryFor, toggle as toggleDownload } from '$lib/offline/manager.svelte';
  import { downloadKey, percentOf } from '$lib/offline/index-state';

  const route = $derived(nav.route.name === 'title' ? nav.route : null);
  const kind = $derived<MediaKind>(route?.kind ?? 'movie');
  const imdbId = $derived(route?.imdbId ?? '');

  let data = $state<TitleDetail | null>(null);
  let loading = $state(true);
  let error = $state('');
  let expanded = $state(false);
  let logoFailed = $state(false);

  /* ── Load ─────────────────────────────────────────────────────────────── */

  $effect(() => {
    const id = imdbId;
    const k = kind;
    if (!id) return;

    const abort = new AbortController();
    loading = true;
    error = '';
    data = null;
    expanded = false;
    logoFailed = false;

    void detail(k, id, abort.signal)
      .then((found) => {
        if (abort.signal.aborted) return;
        data = found;
        loading = false;
        const item: CatalogItem = { id: found.id, kind: found.kind, name: found.name };
        if (found.year) item.year = found.year;
        if (found.poster) item.poster = found.poster;
        rememberItem(item);
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        error = (err as Error).message || 'That title could not be loaded.';
        loading = false;
      });

    return () => abort.abort();
  });

  /* ── Seasons ──────────────────────────────────────────────────────────── */

  const seasons = $derived(data?.seasons ?? []);

  /** Route wins; then the season you were last in; then the first real season. */
  const season = $derived.by(() => {
    if (!seasons.length) return null;
    const wanted = route?.season ?? null;
    if (wanted !== null && seasons.some((s) => s.season === wanted)) return wanted;
    const last = lastEpisodeOf(imdbId);
    if (last?.season !== undefined && seasons.some((s) => s.season === last.season)) return last.season;
    return (seasons.find((s) => s.season > 0) ?? seasons[0]!).season;
  });

  const episodes = $derived(seasons.find((s) => s.season === season)?.episodes ?? []);

  function pickSeason(next: number): void {
    go(titleHref('series', imdbId, next), { replace: true, keepScroll: true });
  }

  /* ── The trailer ──────────────────────────────────────────────────────── */

  /*
   * WHICH trailer, not whether. The action row used to carry a single "Trailer"
   * button that could only ever open the first one, and a title with four
   * trailers had three the interface would not admit to. They are a shelf now,
   * under the cast, and this holds the one being watched.
   */
  let playingTrailer = $state<string | null>(null);

  /** Prizes this title has won. Data CC0 from Wikidata; the marks are ours. */
  const titleAwards = $derived(awardsFor(data?.id));

  /**
   * The age certification — same provenance as the awards (Wikidata, CC0, see
   * `certs.svelte.ts`) and the same honesty rule: drawn only when the table
   * actually answers for this title, which is most films and few series.
   */
  const cert = $derived(certOf(data?.id));

  /*
   * More like this. Cinemeta has no "similar titles" endpoint, so this is the
   * honest approximation available: the first genre this title claims, minus
   * the title itself. The heading says the genre out loud rather than promising
   * a recommendation engine that does not exist behind it.
   */
  let similar = $state<CatalogItem[]>([]);
  const likeGenre = $derived(data?.genres?.[0] ?? null);
  $effect(() => {
    const genre = likeGenre;
    const self = data?.id;
    const k = kind;
    if (!genre || !self) return;
    let live = true;
    void catalog({ kind: k, catalog: 'top', genre })
      .then((items) => {
        if (live) similar = items.filter((i) => i.id !== self).slice(0, 20);
      })
      .catch(() => {
        if (live) similar = [];
      });
    return () => {
      live = false;
    };
  });

  /* ── What the big button does ─────────────────────────────────────────── */

  interface Target {
    href: string;
    label: string;
    detail: string;
    ref: ContentRef;
    episode?: Episode;
  }

  const target = $derived.by<Target | null>(() => {
    if (!data) return null;

    if (kind === 'movie') {
      const ref: ContentRef = { kind: 'movie', imdbId, title: data.name };
      const progress = resumeFor(ref);
      const resuming = progress && !progress.finished && progress.positionSec > 30;
      return {
        href: watchHref({ kind: 'movie', imdbId }),
        label: resuming ? 'Resume' : 'Play',
        // Only when it says something the meta row above does not. "136 min"
        // printed twice on every film page; "1h 49m left" earns its place.
        detail: resuming ? remaining(progress.positionSec, progress.durationSec) : '',
        ref
      };
    }

    // Series: carry on from the last episode this browser touched, or start at one.
    const last = lastEpisodeOf(imdbId);
    let chosen: Episode | undefined;
    let resuming = false;

    if (last?.season !== undefined && last.episode !== undefined) {
      const started = findEpisode(seasons, last.season, last.episode);
      if (started && !last.finished && last.positionSec > 30) {
        chosen = started;
        resuming = true;
      } else if (started) {
        // Finished it — offer the next one.
        const index = seasons.findIndex((s) => s.season === started.season);
        const inSeason = seasons[index]?.episodes ?? [];
        const at = inSeason.findIndex((e) => e.episode === started.episode);
        chosen = inSeason[at + 1] ?? seasons[index + 1]?.episodes[0] ?? started;
      }
    }
    chosen ??= episodes[0] ?? seasons.find((s) => s.season > 0)?.episodes[0] ?? seasons[0]?.episodes[0];
    if (!chosen) return null;

    const ref: ContentRef = {
      kind: 'series',
      imdbId,
      season: chosen.season,
      episode: chosen.episode,
      title: data.name
    };
    const progress = resumeFor(ref);
    return {
      href: watchHref({ kind: 'series', imdbId, season: chosen.season, episode: chosen.episode }),
      label: resuming ? 'Resume' : 'Play',
      detail: `${episodeCode(chosen.season, chosen.episode)} · ${chosen.title}${
        resuming && progress ? ` · ${remaining(progress.positionSec, progress.durationSec)}` : ''
      }`,
      ref,
      episode: chosen
    };
  });

  /* ── Warm the candidate cache while the page is being read ────────────── */

  let prefetched = $state('');
  $effect(() => {
    const t = target;
    if (!t || gate.state !== 'open') return;
    const key = `${t.ref.imdbId}:${t.ref.season ?? ''}:${t.ref.episode ?? ''}`;
    if (key === prefetched) return;
    prefetched = key;
    // 6–10 s cold, and it is almost always finished before Play is pressed.
    prefetch(t.ref);
  });

  /* This page only chooses content. Watch opens a room of one when playback
   * starts; the creator account owns privacy and explicit invitations. */
  const meta = $derived.by(() => {
    if (!data) return [];
    const parts: string[] = [];
    if (data.year) parts.push(data.year);
    if (kind === 'series' && seasons.length) parts.push(count(seasons.filter((s) => s.season > 0).length, 'season'));
    if (data.runtimeText) parts.push(data.runtimeText);
    if (data.status && kind === 'series') parts.push(data.status);
    return parts;
  });

  const blurbLong = $derived((data?.description ?? '').length > 260);

  /* ── Saving it to My Jovii ─────────────────────────────────────────────── */

  // `isSaved` reads the list, so this follows it without being told.
  const kept = $derived(data !== null && isSaved(data.id));

  /**
   * Saves the CATALOGUE ITEM and not the id. My Jovii has to draw its shelf with
   * no network at all, and — more usefully — the genres saved here are what the
   * suggestion rails are derived from later.
   */
  function keep(): void {
    if (!data) return;
    const item: CatalogItem = { id: data.id, kind: data.kind, name: data.name };
    if (data.year) item.year = data.year;
    if (data.poster) item.poster = data.poster;
    if (data.genres?.length) item.genres = data.genres;
    if (data.imdbRating) item.imdbRating = data.imdbRating;
    toggleSaved(item);
  }

  /** The room that ended under us, if this screen has not yet said so. See below. */
  const ended = $derived(endedRoom());

  /* ── The phone's icon row ─────────────────────────────────────────────── */

  /**
   * FOUR EQUAL COLUMNS UNDER PLAY, and nothing that looks like a button.
   *
   * These are Netflix's four — My List, Rate, Share, Download — and the reason
   * they are drawn glyphs over dim labels rather than the pills the desktop row
   * uses is that four pills across 390px are four half-words. On the phone the
   * row IS the secondary action set; the desktop keeps its pills (`.secondary-
   * actions` below), because a mouse can afford a label and a 1520px page has
   * the width for one.
   */

  const rating = $derived(data ? ratingOf(data.id) : null);

  function thumb(): void {
    if (data) rate(data.id, 'up');
  }

  /**
   * Share, or copy. `navigator.share` is the right thing on a phone and does not
   * exist on most desktops, is gated on a transient user gesture everywhere, and
   * is refused outright when nothing in the payload is shareable. All three
   * failures land in the same place: the clipboard, and the notice line this
   * page already uses for "Copied".
   */
  let notice = $state('');
  let noticeTimer: ReturnType<typeof setTimeout> | null = null;
  function say(message: string): void {
    notice = message;
    if (noticeTimer) clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => (notice = ''), 2600);
  }
  $effect(() => () => {
    if (noticeTimer) clearTimeout(noticeTimer);
  });

  async function share(): Promise<void> {
    if (!data) return;
    const url = new URL(titleHref(data.kind, data.id), location.href).href;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: data.name, url });
        return;
      } catch (err) {
        // AbortError is somebody closing the sheet, which is not a failure and
        // must not fall through to copying a link they decided against.
        if ((err as Error)?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      say('Link copied');
    } catch {
      say(url);
    }
  }

  /* ── Downloads ────────────────────────────────────────────────────────── */

  $effect(() => {
    void bootDownloads();
  });

  /** The download row for what Play would open, if there is one. */
  const download = $derived.by(() => {
    void downloads.items;
    return target ? entryFor(downloadKey(target.ref)) : undefined;
  });

  /**
   * "Download" on a film; "Download Season 3" on a series, naming the season the
   * chips are currently showing. Netflix's label, and it is the honest one: the
   * button does not download the series, it downloads what is on screen.
   */
  const downloadLabel = $derived.by(() => {
    if (download?.state === 'done') return 'Downloaded';
    /* "Converting" once the remuxer has started writing, because that is what
       is happening: the file is being rewritten to MP4 on this device so the
       phone can open it (lib/offline/remux.ts). Calling the whole job
       "Downloading" would make its slowest half look like a stalled download. */
    if (download?.state === 'downloading' && download.convertedSec !== undefined) return 'Converting';
    if (download?.state === 'downloading' || download?.state === 'queued') return 'Downloading';
    if (kind === 'movie') return 'Download';
    return season === null || season === 0 ? 'Download' : `Download Season ${season}`;
  });

  function poster(): string | undefined {
    return data?.poster ?? undefined;
  }

  /**
   * A press on the download control.
   *
   * On a film: enqueue it. On a series: enqueue every episode of the selected
   * season IN ORDER, which `enqueueMany` then runs one at a time (see
   * `nextInQueue` for why serial). Pressing it again while something is running
   * cancels that one — the bytes stay, and the Downloads screen is where a
   * delete lives.
   */
  function askDownload(): void {
    // Bound once: `data` is reactive state, and the closure below outlives the
    // narrowing a bare `if (!data) return` gives it.
    const title = data;
    if (!title) return;
    if (download && download.state !== 'failed') {
      if (download.state === 'done') {
        go('/profile#downloads');
        return;
      }
      toggleDownload(download.key);
      return;
    }
    const art = poster();
    if (kind === 'movie') {
      enqueue({
        ref: { kind: 'movie', imdbId: title.id, title: title.name },
        title: title.name,
        ...(art ? { poster: art } : {})
      });
      say('Downloading');
      return;
    }
    if (episodes.length === 0) return;
    enqueueMany(
      episodes.map((e) => ({
        ref: { kind: 'series' as const, imdbId: title.id, season: e.season, episode: e.episode, title: title.name },
        title: title.name,
        episodeLabel: `${episodeCode(e.season, e.episode)} · ${e.title}`,
        ...(art ? { poster: art } : {})
      }))
    );
    say(`${count(episodes.length, 'episode')} queued`);
  }

  /* ── The trailer plays in the hero, on a phone ────────────────────────── */

  /**
   * THE HERO IS THE TRAILER STAGE, not a sheet on top of one.
   *
   * On a phone the 16:9 backdrop at the top of this page is the only place a
   * trailer belongs: a sheet over a 430px screen covers the page you opened to
   * read, and the still it replaces is the same picture at the same size. So the
   * `Trailer` component mounts INSIDE the existing backdrop box — same element,
   * same aspect, no layout change at all — with the mute toggle bottom-right and
   * a close X top-right that puts the still back.
   *
   * ON DESKTOP NOTHING CHANGES. The trailer shelf under the cast and the sheet it
   * opens are untouched: a desktop title page has room for both the artwork and
   * a row of trailers, and the shelf is how a title with four of them admits to
   * having four.
   *
   * IT OBEYS EVERY EXISTING REFUSAL, through `autoplayAllowed` — reduced motion,
   * save-data, a film in progress in this browser, a backgrounded tab
   * (`trailer-rules.ts`) — and it takes the app's single trailer slot through
   * `claimTrailer`, so it cannot run beside the banner's or a tile's. The one
   * refusal added here is the room-ended banner: a sentence explaining that
   * everybody just left should not have a trailer start underneath it.
   */
  $effect(() => watchConditions());

  const TRAILER_OWNER = 'title-hero';

  /** Closed by hand. Sticky per title, so the X is not undone by a re-render. */
  let heroClosed = $state(false);
  /** A shelf press picks WHICH trailer the hero shows; null means the first. */
  let heroPick = $state<string | null>(null);

  /** The trailer the stage is showing: a shelf pick, else the official one. */
  const heroTrailerId = $derived(shell.phone ? (heroPick ?? data?.trailers?.[0] ?? null) : null);
  $effect(() => {
    void data?.id;
    heroClosed = false;
    heroPick = null;
  });

  /**
   * A SHELF PRESS PLAYS IN THE HERO, ON A PHONE.
   *
   * The shelf stays on the phone because a title with four trailers has four and
   * the hero can only show one — but the sheet it used to open is exactly the
   * thing this section was asked to remove from the phone. So the press
   * retargets the stage instead and scrolls back up to it, and the sheet is left
   * to the desktop, where it is still the right answer.
   */
  function playTrailer(yt: string): void {
    if (!shell.phone) {
      playingTrailer = yt;
      return;
    }
    heroPick = yt;
    heroClosed = false;
    heroMuted = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  let heroMuted = $state(true);
  /** True while decoded video is actually on screen, mirrored out of Trailer. */
  let heroShowing = $state(false);

  /*
   * A DELIBERATE PRESS IS NOT SUBJECT TO THE AUTOPLAY REFUSALS. `mayAutoplay` is
   * about starting something nobody asked for (trailer.svelte.ts: "NONE OF THEM
   * STOPS A DELIBERATE PRESS"), so a shelf press shows the stage even under
   * reduced motion or save-data. The room-ended banner still wins: that sentence
   * is why the screen looks the way it does and a video must not cover it.
   */
  const heroTrailerOn = $derived(
    heroTrailerId !== null && !heroClosed && !ended && (heroPick !== null || autoplayAllowed(true, 'hero'))
  );

  $effect(() => {
    if (!heroTrailerOn || heroTrailerId === null) return;
    claimTrailer(TRAILER_OWNER, heroTrailerId);
    return () => releaseTrailer(TRAILER_OWNER);
  });

  /** Resuming rather than restarting, if a tile was playing this a second ago. */
  const heroStartSec = $derived(heroTrailerId ? resumeSecFor(heroTrailerId) : 0);

  function closeHeroTrailer(): void {
    heroClosed = true;
    releaseTrailer(TRAILER_OWNER);
  }

  /* ── "Where did everybody go?" ────────────────────────────────────────── */

  /**
   * THE SENTENCE THAT ARRIVES WITH AN EJECTED GUEST.
   *
   * When the person who started a room leaves it, the room ends for everyone in it, and
   * the watch screen takes its guests out of the player and sends them here — which is
   * where the player's own Back button goes anyway, so it is not a strange place to
   * land. What it IS, without this, is an unexplained one: the film simply stopped and
   * the title page appeared.
   *
   * Read once and gone. `endedRoom()` is set by `noteRoomEnded` on the way out of the
   * watch screen and cleared either by the × or by leaving this page, so a title opened
   * an hour later never announces an evening that finished.
   *
   * `ended` itself is declared ABOVE the hero-trailer block, because that block
   * reads it: a trailer must not start underneath a sentence saying everybody
   * just left.
   */
  $effect(() => () => clearRoomEnded());

  /* The row above paints this title's backdrop as its ground. See top-ground.svelte.ts. */
  $effect(() => {
    const src = data ? (data.background || posterUrl(data.id, 'medium')) : null;
    return claimTopGround(src);
  });
</script>

<svelte:head><title>{data ? `${data.name} — Jovii` : 'Jovii'}</title></svelte:head>

{#if ended}
  <div class="ended" role="status">
    <Icon name="people" size={15} />
    <p>
      The room <b>{ended}</b> has ended. Whoever started it left, so it closed for everyone. Press
      play to carry on watching on your own, or start a new group.
    </p>
    <button type="button" onclick={clearRoomEnded} aria-label="Dismiss">
      <Icon name="close" size={14} />
    </button>
  </div>
{/if}

{#if loading}
  <div class="hero-skeleton">
    <Skeleton ratio="16 / 9" radius="0" />
  </div>
  <div class="pad">
    <Skeleton width="60%" height="1.6rem" radius="7px" />
    <div style="height:10px"></div>
    <Skeleton width="40%" height="0.8rem" radius="5px" />
    <div style="height:20px"></div>
    <Skeleton height="46px" radius="99px" />
    <div style="height:20px"></div>
    <Skeleton height="0.8rem" radius="5px" />
    <div style="height:6px"></div>
    <Skeleton width="90%" height="0.8rem" radius="5px" />
  </div>
{:else if error || !data}
  <div class="pad">
    <Empty icon="alert" tone="bad" title="Could not open that title" detail={error}>
      <Button href="/" icon="back">Back to home</Button>
    </Empty>
  </div>
{:else}
  <article>
    <div class="hero">
      <!--
        THE BACKDROP IS THE TRAILER STAGE ON A PHONE. The Art below is the still
        and stays exactly where it was; `Trailer` is absolutely positioned into
        the same 16:9 box on top of it, so the trailer arriving changes no
        geometry at all — the poster, the title and the Play button do not move
        by a pixel. `noStill` is deliberately NOT set: this surface does not
        cover the refusal branch itself, so Trailer's own still has to be the
        thing that holds the frame when a device declines to autoplay.
      -->
      <div class="backdrop" class:staged={heroTrailerOn}>
        <Art
          src={data.background}
          fallbacks={[posterUrl(data.id, 'medium')]}
          alt=""
          label={data.name}
          ratio="16 / 9"
          maxHeight="min(58dvh, 480px)"
          rounded={false}
          eager
        />
        {#if heroTrailerOn && heroTrailerId}
          <div class="stage">
            {#key heroTrailerId}
              <Trailer
                ytId={heroTrailerId}
                alternates={(data.trailers ?? []).slice(1)}
                still={data.background}
                muted={heroMuted}
                start={heroStartSec}
                label="{data.name} trailer"
                tappable
                onshowing={(showing) => (heroShowing = showing)}
                onended={closeHeroTrailer}
              />
            {/key}
            <!-- Both controls appear only while decoded video is actually on
                 screen. Over the still they would be a lie: there is nothing to
                 mute and nothing to close. -->
            {#if heroShowing}
              <button
                type="button"
                class="stage-btn shut"
                onclick={closeHeroTrailer}
                aria-label="Close the trailer"
              >
                <Icon name="close" size={17} />
              </button>
              <button
                type="button"
                class="stage-btn sound"
                aria-pressed={!heroMuted}
                onclick={() => (heroMuted = !heroMuted)}
                aria-label={heroMuted ? 'Unmute the trailer' : 'Mute the trailer'}
              >
                <Icon name={heroMuted ? 'mute' : 'speaker'} size={17} />
              </button>
            {/if}
          </div>
        {/if}
        <span class="fade" aria-hidden="true"></span>
      </div>

      <div class="head">
        <div class="poster">
          <Art
            src={data.poster}
            fallbacks={[posterUrl(data.id, 'medium')]}
            alt="{data.name} poster"
            label={data.name}
            eager
          />
        </div>
        <div class="naming">
          <h1 class:visually-hidden={data.logo && !logoFailed}>{data.name}</h1>
          {#if data.logo && !logoFailed}
            <img class="logo" src={data.logo} alt="" onerror={() => (logoFailed = true)} />
          {/if}
          <p class="meta">
            {#if data.imdbRating}
              <span class="rating"><Icon name="star" size={13} />{data.imdbRating}</span>
            {/if}
            {#if cert}
              <CertChip {cert} />
            {/if}
            {#each meta as part (part)}<span>{part}</span>{/each}
          </p>
          {#if data.genres?.length}
            <p class="genres">{data.genres.slice(0, 4).join(' · ')}</p>
          {/if}
          <!--
            EVERY prize here, not just the best one. A tile shows one because a
            tile is 136px wide; this is the page you came to in order to decide,
            and the full list is the argument.
          -->
          {#if titleAwards.length > 0}
            <p class="awards">
              {#each titleAwards as a (a.id)}
                <!-- `expanded`: this page IS the argument, so each badge
                     spells out exactly what was won — "Best Picture", not
                     "Oscar winner" (awards.ts holds the precise names; the
                     formal one stays on hover via `label`). -->
                <AwardMark award={a} size={30} expanded />
              {/each}
            </p>
          {/if}
        </div>
      </div>
    </div>

    <div class="actions">
      {#if target}
        <div class="play-action">
          <!--
            WHITE ON A PHONE, CRIMSON ON A DESKTOP. `hero` is a variant of its
            own (Button.svelte) and not a restyled `primary`, because `primary`
            is the brand action at a dozen other call sites and repainting it
            here would repaint all of them.
          -->
          <Button
            href={target.href}
            variant={shell.phone ? 'hero' : 'primary'}
            size="lg"
            icon="play"
            block
          >
            <span class="play-label">{target.label}</span>
            {#if target.detail}<span class="target-detail">{target.detail}</span>{/if}
          </Button>
        </div>

        {#if shell.phone}
          <!--
            THE ICON ROW — four equal columns, drawn glyphs, dim 11px labels, no
            pills. See the note above `rating` in the script for why the phone
            gets this and the desktop keeps its labelled buttons.
          -->
          <div class="icon-row">
            <button
              type="button"
              class="icon-act"
              aria-pressed={kept}
              onclick={keep}
              aria-label={`${kept ? 'Remove' : 'Save'} ${data.name} ${kept ? 'from' : 'to'} My Jovii`}
            >
              <Icon name={kept ? 'check' : 'plus'} size={24} />
              <span>{kept ? 'Saved' : 'My List'}</span>
            </button>

            <button
              type="button"
              class="icon-act"
              aria-pressed={rating === 'up'}
              onclick={thumb}
              aria-label={rating === 'up' ? `Take back your rating of ${data.name}` : `Rate ${data.name}`}
            >
              <Icon name={rating === 'up' ? 'thumbed' : 'thumb'} size={24} />
              <span>{rating === 'up' ? 'Rated' : 'Rate'}</span>
            </button>

            <button type="button" class="icon-act" onclick={() => void share()} aria-label={`Share ${data.name}`}>
              <Icon name="share" size={24} />
              <span>Share</span>
            </button>

            <button
              type="button"
              class="icon-act"
              class:busy={download?.state === 'downloading' || download?.state === 'queued'}
              style="--pct: {download ? percentOf(download) : 0}"
              onclick={askDownload}
              aria-label={`${downloadLabel} ${data.name}`}
            >
              <Icon
                name={download?.state === 'done' ? 'downloaded' : 'download'}
                size={24}
              />
              <span>{downloadLabel}</span>
            </button>
          </div>
          {#if notice}<p class="notice" role="status">{notice}</p>{/if}
          {#if downloads.notice}<p class="notice bad" role="status">{downloads.notice}</p>{/if}
        {:else}
        <div class="secondary-actions">
        <Button
          icon="link"
          variant="ghost"
          size="lg"
          href={watchHref({
            kind,
            imdbId,
            season: target.ref.season ?? null,
            episode: target.ref.episode ?? null,
            pick: true
          })}
          title="Choose picture quality"
        >
          Picture quality
        </Button>
        <button
          type="button"
          class="keep"
          aria-pressed={kept}
          title={kept ? 'Saved to My Jovii' : 'Save to My Jovii'}
          aria-label={`${kept ? 'Remove' : 'Save'} ${data.name} ${kept ? 'from' : 'to'} My Jovii`}
          onclick={keep}
        >
          <Icon name={kept ? 'saved' : 'bookmark'} size={18} />
          <span>{kept ? 'Saved' : 'Save'}</span>
        </button>
        </div>
        {/if}
      {:else}
        <p class="unplayable">No episodes are listed for this yet.</p>
      {/if}
    </div>

    {#if data.description}
      <div id="title-description" class="blurb" class:clipped={blurbLong && !expanded}>
        <p>{data.description}</p>
      </div>
      {#if blurbLong}
        <button type="button" class="more" aria-expanded={expanded} aria-controls="title-description" onclick={() => (expanded = !expanded)}>
          {expanded ? 'Read less' : 'Read more'}
          <Icon name={expanded ? 'up' : 'down'} size={14} />
        </button>
      {/if}
    {/if}


    <!--
      THE TRAILERS ARE A SHELF, NOT A BUTTON. There was one "Trailer" action in
      the row above and it could only ever open the first of them, so a title
      with four had three the interface would not admit to. Side by side, with
      their own stills, they are browsable — and picking one is the same gesture
      as picking anything else on this page.
    -->
    {#if (data.trailers?.length ?? 0) > 0}
      <section class="trailers">
        <h2>Trailers</h2>
        <!-- One d-pad row, like every other shelf: Left/Right walk the stills,
             Up/Down leave for the cast or the action row. -->
        <div class="reel" data-dpad="row">
          {#each data.trailers ?? [] as yt, i (yt)}
            <button
              type="button"
              class="tr"
              onclick={() => playTrailer(yt)}
              title="Play {i === 0 ? 'the trailer' : `trailer ${i + 1}`}"
            >
              <span class="shot">
                <img src={trailerStill(yt)} alt="" loading="lazy" decoding="async" />
                <span class="pip"><Icon name="play" size={16} /></span>
              </span>
              <span class="cap">{i === 0 ? 'Official trailer' : `Trailer ${i + 1}`}</span>
            </button>
          {/each}
        </div>
      </section>
    {/if}

    <!-- The cast, as faces rather than a comma-separated line (docs/CAST.md).
         The row fetches for itself and renders nothing when no source knows the
         title, so mounting is unconditional. -->
    <CastRow kind={data.kind} imdbId={data.id} names={data.cast} moviedbId={data.moviedbId} />



    {#if data.director.length}
      <dl class="credits">
        <dt>{data.director.length > 1 ? 'Directors' : 'Director'}</dt>
        <dd>
          <!-- Each name is an internal link to /person/:name — the page
               resolves the name against Wikidata's occupation-filtered search
               on arrival (lib/ui/person.ts), so a wrong-person guess is made
               nowhere and a name that resolves to nobody still renders. -->
          {#each data.director.slice(0, 3) as director, i (director)}{#if i > 0}{', '}{/if}<a
              class="credit-link"
              href={personHref({ name: director })}>{director}</a
            >{/each}
        </dd>
      </dl>
    {/if}

    {#if kind === 'series'}
      {#if seasons.length === 0}
        <Empty icon="tv" title="No episodes listed" detail="Cinemeta has no episode data for this series yet." />
      {:else}
        <section class="episodes">
          <div class="seasons" data-dpad="row" role="group" aria-label="Seasons">
            {#each seasons as s (s.season)}
              <Chip interactive selected={s.season === season} onclick={() => pickSeason(s.season)}>
                {s.season === 0 ? 'Specials' : `Season ${s.season}`}
              </Chip>
            {/each}
          </div>

          <p class="ep-count">{count(episodes.length, 'episode')}</p>

          <!--
            `data-dpad="col"`: a stacked d-pad group, so a remote coming DOWN
            from the season chips steps INTO the episodes rather than leaping
            clean over all of them to the rail below — measured at 1280x720,
            Down went chip -> "More Drama series", 1400px in one press, and the
            whole season was unreachable in either direction. The rules treat a
            'col' group's top and bottom as openings back onto the page, so
            Up from episode one reaches the chips again.
          -->
          <ul class="ep-list" data-dpad="col">
            {#each episodes as episode (episode.id)}
              <li>
                <EpisodeItem
                  {episode}
                  runtimeText={data.runtimeText}
                  current={route?.season === episode.season && route?.episode === episode.episode}
                  seriesName={data.name}
                  poster={data.poster}
                />
              </li>
            {/each}
          </ul>
        </section>
      {/if}
    {/if}

    <!--
      More like this, LAST — under the episode selector on a series, under the
      credits on a film. It is what you read when you have decided this is not
      the one, so it belongs after everything that might still persuade you.
      Named by the genre it actually queried: there is no recommendation engine
      behind it and the heading should not imply one.
    -->
    {#if similar.length > 0}
      <Rail title="More {likeGenre} {data.kind === 'series' ? 'series' : 'films'}">
        {#each similar as item (item.id)}
          <!-- Kind-scoped by construction (the query carries data.kind), so the
               kind tag would say what the rail title already says. -->
          <Poster {item} tagKind={false} />
        {/each}
      </Rail>
    {/if}
  </article>

  {#if playingTrailer}
    <!-- `alternates` because ~1 title in 10 has embedding disabled by the uploader
         and a third carry a second id; `still` because our own 16:9 backdrop is
         what holds the screen while YouTube's branding goes away. -->
    <TrailerSheet
      ytId={playingTrailer}
      alternates={(data.trailers ?? []).filter((t) => t !== playingTrailer)}
      still={data.background}
      name={data.name}
      watchLabel={data.kind === 'series' ? 'Watch series' : 'Watch movie'}
      onwatch={() => {
        playingTrailer = null;
        if (target) go(target.href);
      }}
      onclose={() => (playingTrailer = null)}
    />
  {/if}
{/if}

<style>
  .awards {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: var(--space-2);
  }

  /* ── The trailer shelf ─────────────────────────────────────────────────────
   * Side by side and scrollable, like every other shelf on this page. The still
   * is YouTube's for these and only these: a title has ONE backdrop, so using it
   * for four trailers would draw the same picture four times and say nothing.
   * `docs/TRAILERS.md` § E2 settled the size — mqdefault, ~20 KB.
   *
   * ALIGNED LIKE THE CAST ROW, and by the same mechanism: this section carried
   * no `--page-pad` at all, so the heading and the first still sat flush on the
   * glass at x=0 — measured 14px left of every other block on a phone, 32px at
   * desktop — which the owner read, correctly, as the shelf being cut off. The
   * pad lives INSIDE the scroller so the reel still scrolls edge to edge, and
   * `scroll-padding-inline` keeps the snap points on the pad, not the glass —
   * without it every snap re-created the defect this comment records.
   */
  .trailers {
    margin-top: var(--space-5);
  }
  .trailers h2 {
    padding: 0 var(--page-pad);
    font-size: var(--text-lg);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    margin-bottom: var(--space-3);
  }
  .reel {
    display: flex;
    gap: var(--gap);
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    scroll-padding-inline: var(--page-pad);
    padding: 0 var(--page-pad) var(--space-2);
  }
  .tr {
    flex: 0 0 auto;
    width: 216px;
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .tr .shot {
    position: relative;
    display: block;
    aspect-ratio: 16 / 9;
    border-radius: var(--radius-xs);
    overflow: hidden;
    background: var(--surface-2);
    box-shadow: var(--shadow-1);
    transition: transform var(--dur-fast) var(--ease);
  }
  .tr .shot img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .tr .pip {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    color: var(--text);
    background: linear-gradient(to top, rgb(0 0 0 / 0.5), transparent 60%);
  }
  .tr:hover .shot,
  .tr:focus-visible .shot {
    transform: translateY(-2px);
  }
  .tr:focus-visible .shot {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }
  .tr .cap {
    font-size: var(--text-xs);
    color: var(--text-dim);
  }
  @media (prefers-reduced-motion: reduce) {
    .tr .shot {
      transition: none;
    }
    .tr:hover .shot,
    .tr:focus-visible .shot {
      transform: none;
    }
  }

  .pad {
    padding: 20px var(--page-pad);
  }

  /* The room-ended line. In the flow above the hero rather than floating over it: it
     is a sentence about something that has already happened, not a control. */
  .ended {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 10px var(--page-pad) 0;
    padding: 9px 9px 9px 13px;
    border-radius: var(--radius);
    border: 1px solid var(--line);
    background: var(--surface-2);
    color: var(--text);
    font-size: var(--text-xs);
    line-height: 1.45;
  }
  .ended p {
    flex: 1;
    margin: 0;
    text-wrap: pretty;
  }
  .ended b {
    font-family: var(--mono);
    font-weight: 600;
  }
  .ended button {
    display: grid;
    place-content: center;
    flex: none;
    width: var(--hit);
    height: var(--hit);
    border-radius: var(--radius-pill);
    color: var(--text-faint);
  }
  .ended button:hover {
    background: var(--surface-3);
    color: var(--text);
  }
  .hero-skeleton {
    margin-top: calc(var(--chrome-float) * -1);
  }

  /* The backdrop runs under the floating chrome, on a phone as well as a desktop. */
  .hero {
    position: relative;
    margin-top: calc(var(--chrome-float) * -1);
  }

  .backdrop {
    position: relative;
    opacity: 0.5;
  }
  /*
   * A TRAILER IS THE PICTURE, NOT SCENERY. The resting backdrop is held at 50%
   * so the title and the meta line read over it; a trailer that is actually
   * playing gets its full brightness back, because it IS what you are looking at
   * for the next thirty seconds.
   */
  .backdrop.staged {
    opacity: 1;
  }
  /* The stage fills the Art's own box: same element, same 16:9, no reflow when
     the trailer arrives or leaves. */
  .stage {
    position: absolute;
    inset: 0;
    overflow: hidden;
  }
  .stage-btn {
    position: absolute;
    z-index: 2;
    display: grid;
    place-content: center;
    width: var(--hit);
    height: var(--hit);
    border-radius: var(--radius-pill);
    border: 1px solid var(--glass-line);
    background: rgb(8 10 15 / 0.55);
    backdrop-filter: var(--glass-blur);
    color: var(--text);
  }
  .stage-btn.shut {
    top: calc(var(--chrome-float) + var(--space-2));
    right: var(--space-3);
  }
  .stage-btn.sound {
    right: var(--space-3);
    bottom: var(--space-3);
  }
  .stage-btn:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  .fade {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(to top, var(--bg) 2%, color-mix(in oklab, var(--bg) 55%, transparent) 46%, transparent 88%),
      linear-gradient(to right, var(--bg) 0%, transparent 34%);
  }

  .head {
    position: relative;
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr);
    align-items: end;
    gap: var(--space-4);
    padding: 0 var(--page-pad);
    margin-top: -58px;
  }
  .poster {
    border-radius: var(--radius);
    overflow: hidden;
    box-shadow: 0 10px 30px rgb(0 0 0 / 0.55);
    border: 1px solid color-mix(in oklab, var(--line) 70%, transparent);
  }

  .naming {
    min-width: 0;
    padding-bottom: 3px;
  }
  h1 {
    font-size: 1.42rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.025em;
    text-wrap: balance;
  }
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .logo {
    max-height: 62px;
    max-width: 100%;
    width: auto;
    object-fit: contain;
    object-position: left bottom;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    margin-top: var(--space-3);
    color: var(--text-dim);
    font-size: var(--text-sm);
    line-height: 1.5;
  }
  .rating {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: var(--warn);
    font-weight: 600;
  }
  .genres {
    margin-top: var(--space-2);
    color: var(--text-dim);
    font-size: var(--text-sm);
    line-height: 1.5;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3) var(--space-4);
    padding: var(--space-5) var(--page-pad) 0;
  }
  .play-action {
    flex: 1 1 100%;
    min-width: 0;
  }
  .play-action :global(.btn.primary) {
    height: auto;
    min-height: 56px;
    padding: var(--space-3) var(--space-5);
    white-space: normal;
    gap: var(--space-3);
  }
  .play-action :global(.btn.primary > span) {
    min-width: 0;
  }
  .play-label {
    display: block;
    font-size: var(--text-lg);
  }
  /*
   * THE ICON ROW — four equal columns and nothing that reads as a button.
   *
   * `grid-template-columns: repeat(4, 1fr)` and not a flex row with gaps: the
   * four labels are different lengths ("My List", "Rate", "Share", "Download
   * Season 3") and a flex row sizes each column to its own word, which puts the
   * four glyphs at four unequal positions. Equal columns keep the glyphs on an
   * even rhythm and let the longest label wrap under its own icon.
   *
   * NO PILLS, NO BACKGROUNDS, NO BORDERS. The whole column is the hit target —
   * 44px of height is guaranteed by the min-height, well past the floor — and
   * the pressed state is the glyph swapping to its filled twin, not a plate
   * appearing under it.
   */
  .icon-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    align-items: start;
    flex: 1 1 100%;
    gap: var(--space-2);
    margin-top: var(--space-2);
  }
  .icon-act {
    display: grid;
    justify-items: center;
    align-content: start;
    gap: 6px;
    min-height: calc(var(--hit) + 14px);
    padding: var(--space-2) 2px;
    background: none;
    border: 0;
    border-radius: var(--radius);
    color: var(--text-dim);
    font-size: var(--text-2xs);
    font-weight: var(--weight-medium);
    line-height: 1.25;
    text-align: center;
    text-wrap: balance;
    touch-action: manipulation;
    transition: color var(--dur-fast) var(--ease);
  }
  .icon-act:hover {
    color: var(--text);
  }
  .icon-act:active :global(svg) {
    transform: scale(0.9);
  }
  .icon-act :global(svg) {
    transition: transform var(--dur-fast) var(--ease);
  }
  .icon-act[aria-pressed='true'] {
    color: var(--text);
  }
  .icon-act:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -2px;
  }

  /*
   * THE DOWNLOAD COLUMN WHILE IT IS WORKING. A ring around the glyph rather than
   * a bar under the row: the progress belongs to that one action, and a bar
   * spanning the row would claim the other three are downloading too. Same
   * conic-gradient-in-a-mask recipe as the Downloads screen's ring, so the two
   * places a download's progress is drawn cannot drift.
   */
  .icon-act.busy {
    position: relative;
    color: var(--text);
  }
  .icon-act.busy::before {
    content: '';
    position: absolute;
    top: calc(var(--space-2) - 5px);
    /* Horizontally centred on the column; it had only a top offset and so sat
       at the column's left edge (the owner's screenshot). */
    left: 50%;
    translate: -50% 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: conic-gradient(
      var(--accent) calc(var(--pct, 0) * 1%),
      color-mix(in oklab, var(--text-faint) 34%, transparent) 0
    );
    -webkit-mask: radial-gradient(circle, transparent 14px, #000 14.5px);
    mask: radial-gradient(circle, transparent 14px, #000 14.5px);
  }

  /* The one-line answer to a press that had no visible result of its own: a
     copied link, a queued season, a refused download. Same shape as the notices
     everywhere else in the app. */
  .notice {
    flex: 1 1 100%;
    margin-top: var(--space-1);
    color: var(--text-faint);
    font-size: var(--text-xs);
    line-height: var(--leading-snug);
    overflow-wrap: anywhere;
  }
  .notice.bad {
    color: var(--bad);
  }

  .secondary-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
  }
  .secondary-actions :global(.btn) {
    min-height: var(--hit);
    padding-inline: var(--space-3);
    font-size: var(--text-sm);
  }

  /* The save toggle. Same silhouette pressed and unpressed, so it does not jump. */
  .keep {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    min-width: 96px;
    min-height: var(--hit);
    padding: var(--space-2) var(--space-4);
    flex: none;
    font-size: var(--text-sm);
    font-weight: var(--weight-semi);
    border-radius: var(--radius-pill);
    border: 1px solid var(--line);
    background: var(--surface-2);
    color: var(--text-dim);
    transition:
      color var(--dur) var(--ease),
      background-color var(--dur) var(--ease),
      border-color var(--dur) var(--ease);
  }
  .keep:hover {
    background: var(--surface-3);
    color: var(--text);
  }
  .keep[aria-pressed='true'] {
    color: var(--text);
    border-color: var(--text-faint);
    background: var(--surface-3);
  }
  .unplayable {
    color: var(--text-faint);
    font-size: 0.83rem;
  }

  .target-detail {
    display: block;
    margin-top: var(--space-1);
    font-size: var(--text-sm);
    font-weight: var(--weight-normal);
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  .blurb {
    padding: 18px var(--page-pad) 0;
    color: var(--text-dim);
    font-size: var(--text-base);
    line-height: 1.65;
    max-width: 72ch;
  }
  .blurb.clipped p {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .more {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    min-height: var(--hit);
    margin: var(--space-1) var(--page-pad) 0;
    color: var(--text-dim);
    font-size: var(--text-sm);
    font-weight: 500;
  }
  .more:hover {
    color: var(--text);
  }

  .credits {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 3px 12px;
    margin: 18px 0 0;
    padding: 0 var(--page-pad);
    font-size: 0.79rem;
    line-height: 1.5;
  }
  .credits dt {
    color: var(--text-faint);
  }
  .credits dd {
    margin: 0;
    color: var(--text-dim);
    min-width: 0;
  }
  /* A director's name goes to their person page. Quiet by default — the
     credits line is a footnote, not a call to action — legible as a link on
     hover and to the ring on focus. */
  .credit-link {
    color: inherit;
    text-decoration: underline;
    text-decoration-color: color-mix(in oklab, currentcolor 35%, transparent);
    text-underline-offset: 2px;
  }
  .credit-link:hover {
    color: var(--text);
    text-decoration-color: currentcolor;
  }
  .credit-link:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
    border-radius: 3px;
  }

  .episodes {
    margin-top: 28px;
    padding-bottom: 40px;
  }
  .seasons {
    display: flex;
    gap: 6px;
    padding: 0 var(--page-pad) 12px;
    overflow-x: auto;
    scrollbar-width: none;
    border-bottom: 1px solid var(--line);
  }
  .seasons::-webkit-scrollbar {
    display: none;
  }
  .ep-count {
    padding: 12px var(--page-pad) 2px;
    color: var(--text-faint);
    font-size: 0.74rem;
  }
  .ep-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  @media (min-width: 700px) {
    .head {
      grid-template-columns: 132px 1fr;
      gap: 20px;
      margin-top: -84px;
    }
    h1 {
      font-size: 2rem;
    }
    .logo {
      max-height: 90px;
    }
    .play-action {
      flex: 0 1 380px;
    }
    .play-action :global(.btn.primary) {
      justify-content: flex-start;
      text-align: left;
    }
  }
  @media (min-width: 1000px) {
    .backdrop {
      opacity: 0.62;
    }
  }
</style>
